import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Col, Empty, Modal, Row, Table, Tag, Typography } from 'antd';
import { EyeOutlined, HistoryOutlined } from '@ant-design/icons';
import {
  IProfessional,
  ITreatment,
  ITreatmentLog,
} from '../../../../shared/interfaces';
import { AButton, ARate, Loading } from '../../../../components';
import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';
import { formatDate } from '../../../../shared/utils/format-date';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { Line, getElementAtEvent } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

interface IHistoricTreatmentProps {
  open: boolean;
  close: () => void;
  treatment: ITreatment | null;
}

const HistoricTreatment: React.FC<IHistoricTreatmentProps> = ({
  open,
  close,
  treatment,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState<boolean>(true);
  const [treatmentHistoric, setTreatmentHistoric] = useState<ITreatmentLog[]>(
    [],
  );
  const [selectedLog, setSelectedLog] = useState<ITreatmentLog | null>(null);
  const [modalDetails, setModalDetails] = useState<boolean>(false);
  const chartRef = useRef<any>(null);

  const statusLabel: Record<number, string> = {
    0: 'Pendente',
    1: 'Em andamento',
    2: 'Concluído',
  };

  const statusColor: Record<number, string> = {
    0: 'default',
    1: 'processing',
    2: 'success',
  };

  const loadHistoric = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      api.get(`/treatments-logs/list/${treatment?.id}`),
      api.get('/professionals/select'),
    ])
      .then(([historicResponse, professionalsResponse]) => {
        const professionals = professionalsResponse.data as IProfessional[];
        const normalizedHistoric = (historicResponse.data || []).map(
          (item: ITreatmentLog) => ({
            ...item,
            signed_professional_name:
              professionals.find((prof) => prof.id === item.professional_id)
                ?.name || undefined,
          }),
        );
        setTreatmentHistoric(normalizedHistoric);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `${error.response.data.message}`,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const sortedHistoric = useMemo(
    () =>
      [...treatmentHistoric].sort((a, b) => {
        const first = new Date(`${a.date}T${a.time || '00:00:00'}`).valueOf();
        const second = new Date(`${b.date}T${b.time || '00:00:00'}`).valueOf();
        return first - second;
      }),
    [treatmentHistoric],
  );

  const averageRank = useMemo(() => {
    if (!treatmentHistoric.length) return 0;
    const total = treatmentHistoric.reduce(
      (acc, item) => acc + Number(item.rank || 0),
      0,
    );
    return total / treatmentHistoric.length;
  }, [treatmentHistoric]);

  const latestLog = useMemo(
    () => sortedHistoric[sortedHistoric.length - 1],
    [sortedHistoric],
  );

  const selectedSignatureCode = selectedLog?.signature_validation_code;
  const selectedSignedAt = selectedLog?.signed_at
    ? formatDate(selectedLog.signed_at, 'DD/MM/YYYY HH:mm:ss')
    : null;

  const chartData: ChartData<'line', any, string> = useMemo(
    () => ({
      labels: sortedHistoric.map((log) =>
        `${formatDate(log.date)} ${log.time || ''}`.trim(),
      ),
      datasets: [
        {
          label: 'Rank da evolução',
          data: sortedHistoric.map((log) => ({
            ...log,
            x: `${formatDate(log.date)} ${log.time || ''}`.trim(),
            y: Number(log.rank || 0),
          })),
          borderColor: 'rgb(22, 119, 255)',
          backgroundColor: 'rgba(22, 119, 255, 0.25)',
          tension: 0.3,
          pointRadius: 5,
          pointHoverRadius: 7,
        },
      ],
    }),
    [sortedHistoric],
  );

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      intersect: true,
    },
    scales: {
      y: {
        max: 10,
        min: 0,
        ticks: {
          stepSize: 1,
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const raw = context.raw as ITreatmentLog & { y: number };
            const status = statusLabel[Number(raw?.status) || 0];
            return `Rank: ${raw?.y ?? 0} | Status: ${status}`;
          },
          afterLabel: (context) => {
            const raw = context.raw as ITreatmentLog;
            return `Obs.: ${raw?.observation || '-'}`;
          },
        },
      },
    },
  };

  const handleChartClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
      if (!chartRef.current) return;
      const points = getElementAtEvent(chartRef.current, event);
      if (!points.length) return;

      const point = points[0];
      const raw = chartData.datasets[0].data[point.index] as ITreatmentLog;
      if (!raw) return;

      setSelectedLog(raw);
      setModalDetails(true);
    },
    [chartData],
  );

  useEffect(() => {
    loadHistoric();
  }, [open]);

  return (
    <Modal
      title={
        <>
          <HistoryOutlined /> Histórico do Tratamento Nº {treatment?.code}
        </>
      }
      centered
      open={open}
      cancelText="Cancelar"
      onCancel={close}
      width={1000}
      footer={[
        <AButton key="close" type="default" onClick={close}>
          Fechar
        </AButton>,
      ]}
    >
      {loading ? (
        <Loading load />
      ) : (
        <>
          {!treatmentHistoric.length ? (
            <Empty description="Nenhum registro de evolução encontrado" />
          ) : (
            <>
              <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Typography.Text type="secondary">
                      Registros
                    </Typography.Text>
                    <Typography.Title level={3} style={{ margin: 0 }}>
                      {treatmentHistoric.length}
                    </Typography.Title>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Typography.Text type="secondary">
                      Rank médio
                    </Typography.Text>
                    <Typography.Title level={3} style={{ margin: 0 }}>
                      {averageRank.toFixed(1)}
                    </Typography.Title>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card size="small">
                    <Typography.Text type="secondary">
                      Último rank
                    </Typography.Text>
                    <Typography.Title level={3} style={{ margin: 0 }}>
                      {latestLog?.rank ?? 0}
                    </Typography.Title>
                  </Card>
                </Col>
              </Row>

              <Card
                title="Gráfico da evolução"
                size="small"
                style={{ marginBottom: 16 }}
                bodyStyle={{ height: 280 }}
              >
                <Line
                  ref={chartRef}
                  options={chartOptions}
                  data={chartData}
                  onClick={handleChartClick}
                />
              </Card>

              <Table
                scroll={{ y: 290 }}
                columns={[
                  {
                    title: 'Data',
                    dataIndex: 'date',
                    render: (value: string) => formatDate(value),
                    width: 110,
                  },
                  {
                    title: 'Hora',
                    dataIndex: 'time',
                    width: 100,
                  },
                  {
                    title: 'Procedimento',
                    dataIndex: 'procedure_description',
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    width: 130,
                    render: (value: number) => (
                      <Tag color={statusColor[value] || 'default'}>
                        {statusLabel[value] || 'Pendente'}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Rank',
                    dataIndex: 'rank',
                    width: 220,
                    render: (value: number) => (
                      <ARate
                        value={Number(value || 0)}
                        tooltips={[
                          '1',
                          '2',
                          '3',
                          '4',
                          '5',
                          '6',
                          '7',
                          '8',
                          '9',
                          '10',
                        ]}
                        count={10}
                        disabled
                      />
                    ),
                  },
                  {
                    title: 'Observação',
                    dataIndex: 'observation',
                    render: (value: string) => value || '-',
                  },
                  {
                    title: 'Ações',
                    width: 90,
                    render: (row: ITreatmentLog) => (
                      <AButton
                        icon={<EyeOutlined />}
                        onClick={() => {
                          setSelectedLog(row);
                          setModalDetails(true);
                        }}
                      />
                    ),
                  },
                ]}
                dataSource={sortedHistoric}
                rowKey="id"
                pagination={false}
              />
            </>
          )}
        </>
      )}

      <Modal
        title="Detalhes da evolução"
        open={modalDetails}
        onCancel={() => setModalDetails(false)}
        footer={null}
      >
        <Row style={{ marginBottom: 8 }}>
          <Typography.Text strong>Data:</Typography.Text>
          <Typography.Text style={{ marginLeft: 8 }}>
            {selectedLog?.date ? formatDate(selectedLog.date) : '-'}
          </Typography.Text>
        </Row>
        <Row style={{ marginBottom: 8 }}>
          <Typography.Text strong>Hora:</Typography.Text>
          <Typography.Text style={{ marginLeft: 8 }}>
            {selectedLog?.time || '-'}
          </Typography.Text>
        </Row>
        <Row style={{ marginBottom: 8 }}>
          <Typography.Text strong>Procedimento:</Typography.Text>
          <Typography.Text style={{ marginLeft: 8 }}>
            {selectedLog?.procedure_description || '-'}
          </Typography.Text>
        </Row>
        <Row style={{ marginBottom: 8 }}>
          <Typography.Text strong>Status:</Typography.Text>
          <Typography.Text style={{ marginLeft: 8 }}>
            {statusLabel[selectedLog?.status || 0]}
          </Typography.Text>
        </Row>
        <Row style={{ marginBottom: 8, alignItems: 'center' }}>
          <Typography.Text strong>Rank:</Typography.Text>
          <div style={{ marginLeft: 8 }}>
            <ARate
              value={Number(selectedLog?.rank || 0)}
              tooltips={['1', '2', '3', '4', '5', '6', '7', '8', '9', '10']}
              count={10}
              disabled
            />
          </div>
        </Row>
        <Row>
          <Typography.Text strong>Observação:</Typography.Text>
        </Row>
        <Row>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {selectedLog?.observation || '-'}
          </Typography.Paragraph>
        </Row>
        {(selectedSignatureCode || selectedSignedAt) && (
          <Card
            size="small"
            style={{
              marginTop: 16,
              borderColor: '#92d7b1',
              background: '#eefaf3',
            }}
          >
            <Typography.Text
              strong
              style={{ display: 'block', color: '#1f7a4c', marginBottom: 4 }}
            >
              Assinatura digital
            </Typography.Text>
            <Typography.Text style={{ display: 'block', marginBottom: 4 }}>
              Assinado digitalmente por{' '}
              {selectedLog?.signed_professional_name ||
                treatment?.professional_name ||
                'profissional vinculado'}
            </Typography.Text>
            {selectedSignatureCode && (
              <Typography.Text style={{ display: 'block', marginBottom: 2 }}>
                Codigo de validacao: {selectedSignatureCode}
              </Typography.Text>
            )}
            {selectedSignedAt && (
              <Typography.Text style={{ display: 'block' }}>
                Data da assinatura: {selectedSignedAt}
              </Typography.Text>
            )}
          </Card>
        )}
      </Modal>
    </Modal>
  );
};

export default HistoricTreatment;
